import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

// Timeline entry interface
export interface TimelineEntry {
  date: string;
  information: string;
}

// Telecaller info interface (minimal)
export interface TelecallerInfo {
  id: string;
  user_id: string;
  name: string;
  avatar_image?: string;
}

// Program detail interface
export interface ProgramDetail {
  id: string;
  program_id: string;
  program_name: string;
  event_type: string;
  location: string;
  date: string;
  program_manager?: string;
  patients_enrolled?: number;
  organization_id?: string;
  created_at?: string;
  updated_at?: string;
}

// Patient detail interface
export interface PatientDetail {
  name: string;
  email: string;
  age?: number;
  gender?: string;
  contact_number?: string;
  program_ids: string[];
  status: 'active' | 'inactive';
  followup_status?: string;
  therapy_status?: string;
  risk_level?: string;
  followups_completed: number;
  followups_pending: number;
  followups_rescheduled: number;
  next_followup_date?: string;
  last_followup_date?: string;
  next_reminder_date?: string;
  therapy_start_date?: string;
  completion_rate: number;
  award_points: number;
  consent_status?: string;
  consent_channels: string[];
  therapy_notes?: string;
  treatment_notes?: string;
  diagnostics_notes?: string;
  timeline: TimelineEntry[];
  id: string;
  patient_id: string;
  patient_pid?: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  user_id?: string;
  doctor_id?: string;
  telecaller?: TelecallerInfo;
  calls_count: number;
  called_by_telecallers: string[];
  programs: ProgramDetail[];
}

// Pagination info interface
export interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_count: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
}

// Telecaller stats interface
export interface TelecallerStats {
  total_patients: number;
  active_patients: number;
  high_risk_patients: number;
  followups_due_this_week: number;
  followups_completed: number;
  followups_pending: number;
  followups_rescheduled: number;
  total_followups_done: number;
  /** Inbound calls (from Exotel voicelogs) in the date window */
  total_inbound_calls?: number;
  /** Outbound calls (from Exotel voicelogs) in the date window */
  total_outbound_calls?: number;
  /** Lab test bookings for this telecaller's patients in the date window */
  total_tests_booked?: number;
  /** Medicine purchase orders for this telecaller's patients in the date window */
  total_medicine_orders?: number;
  /** New patients assigned to this telecaller in the date window */
  new_patients_added?: number;
}

export interface AnalyticsDataPoint {
  date?: string;
  day?: string;
  value: number;
}

export interface AnalyticsGraph {
  title: string;
  data_points: AnalyticsDataPoint[];
  total: number;
}

export interface AnalyticsSummary {
  total_patients: number;
  active_followups: number;
  overdue_followups: number;
  completion_rate: number;
  /** Inbound calls (Exotel) in the date window */
  total_inbound_calls?: number;
  /** Outbound calls (Exotel) in the date window */
  total_outbound_calls?: number;
  /** Lab test bookings in the date window */
  total_tests_booked?: number;
  /** Medicine purchase orders in the date window */
  total_medicine_orders?: number;
  /** New patients added in the date window */
  new_patients_added?: number;
}

export interface AnalyticsPatientStatus {
  completed: number;
  active: number;
  overdue: number;
}

export interface TelecallerAnalyticsResponse {
  telecaller_id: string;
  telecaller_name: string;
  summary: AnalyticsSummary;
  followup_trends: AnalyticsGraph;
  patient_status_distribution: AnalyticsPatientStatus;
  calls_graph: AnalyticsGraph;
  sales_graph: AnalyticsGraph;
  start_date: string;
  end_date: string;
}

// Telecaller detail response
export interface TelecallerDetailResponse {
  email: string;
  name: string;
  age?: number;
  gender?: string;
  bio?: string;
  role: string;
  role_id: string;
  organization_id: string;
  contact_number?: string;
  avatar_image?: string;
  telecaller_status: string;
  user_id: string;
  id: string;
  created_at: string;
  privileges: string[];
  no_of_patients: number;
  no_of_programs: number;
  patients: PatientDetail[];
  programs: ProgramDetail[];
}

// Telecaller overview response
export interface TelecallerOverviewResponse {
  user_id: string;
  total_patients: number;
  active_patients: number;
  high_risk_patients: number;
  followups_due_this_week: number;
  followups_completed: number;
  followups_pending: number;
  followups_rescheduled: number;
  total_followups_done: number;
  total_purchases: number;
  total_net_amount: number;
  average_order_value: number;
}


// Medicine/Lab test item interface
export interface MedicineItem {
  name: string;
  company: string;
  quantity: number;
  rate: number;
  discount: number;
  duration?: string | null;
  amount: number;
  total: number;
  item_type: 'medicine' | 'lab';
}

// Purchase detail interface for sales report
export interface PurchaseDetail {
  id: string;
  purchase_id?: string;
  patient_id: string;
  patient_name?: string;
  user_id?: string;
  telecaller_name?: string;
  doctor_id?: string;
  doctor_name?: string;
  hospital_id?: string;
  hospital_name?: string;
  lab_id?: string | null;
  lab_name?: string;
  description: string; // API returns 'description' not 'items'
  sold_by?: string; // API returns 'sold_by' not 'updated_by'
  course?: string;
  medicines?: MedicineItem[];
  lab_tests?: MedicineItem[];
  total_amount: number;
  total_discount?: number; // API returns 'total_discount' not 'discount_amount'
  net_total: number; // API returns 'net_total' not 'net_amount'
  net_amount?: number; // Keep for backward compatibility
  payment_status?: string;
  purchase_date: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  receipt_url?: string;
  organization_id?: string;

  // Legacy fields for backward compatibility
  items?: string;
}

// Sales stats interface
export interface SalesStats {
  total_purchases: number;
  total_amount: number;
  total_net_amount: number;
}

// Sales report response
export interface SalesReportResponse {
  purchases: PurchaseDetail[];
  pagination: PaginationInfo;
  stats: SalesStats;
}

// Call log interfaces
export interface CallLogRecord {
  Sid: string;
  ParentCallSid: string;
  DateCreated: string;
  DateUpdated: string;
  AccountSid: string;
  To: string;
  From: string;
  PhoneNumber: string;
  PhoneNumberSid: string;
  /** All possible statuses from the Exotel callback webhook */
  Status: 'completed' | 'busy' | 'failed' | 'no-answer' | 'ringing' | 'in-progress';
  StartTime: string;
  EndTime: string;
  /** Total call duration in seconds */
  Duration: number;
  /** Actual talk/conversation duration in seconds (from Exotel callback) */
  ConversationDuration?: number;
  Price: number;
  Direction: 'inbound' | 'outbound' | 'outbound-api';
  AnsweredBy: string;
  ForwardedFrom: string;
  CallerName: string;
  Uri: string;
  CustomField: string;
  RecordingUrl: string;
  telecaller_document_id: string;
  user_id: string;
  telecaller_name: string;
  telecaller_image: string;
  patient_document_id: string;
  patient_id: string;
  patient_name: string;
  patient_image: string | null;
}

export interface TelecallerDetails {
  telecaller_document_id: string;
  user_id: string;
  telecaller_name: string;
  telecaller_image: string;
}

export interface CallLogsResponse {
  calls: CallLogRecord[];
  total: number;
  telecaller_details: TelecallerDetails;
}

// Telecaller patients response
export interface TelecallerPatientsResponse {
  patients: PatientDetail[];
  programs: ProgramDetail[];
  telecallers: TelecallerInfo[];
  pagination: PaginationInfo;
  stats: TelecallerStats;
}

// Telecaller programs response
export interface TelecallerProgramsResponse {
  user_id: string;
  programs: ProgramDetail[];
}

// Upcoming Follow-ups interfaces
export interface UpcomingFollowup {
  id: string;
  patient_id: string;
  patient_pid?: string;
  name: string;
  contact_number: string;
  avatar_image: string;
  next_followup_date: string;
  program_name: string;
}

export interface UpcomingFollowupsResponse {
  followups: UpcomingFollowup[];
  total: number;
  filter_type: string;
}

export interface TelecallerPerformanceResponse {
  telecaller_id: string;
  period: string;
  calls_made: number;
  avg_call_duration_seconds: number;
  avg_call_duration_display: string;
  total_call_duration_seconds: number;
  completed_calls: number;
  missed_calls: number;
  busy_calls: number;
  failed_calls: number;
}

// Request interfaces
export interface CreateTelecallerRequest {
  email: string;
  name: string;
  age?: number;
  gender?: string;
  bio?: string;
  contact_number?: string;
  avatar_image?: string;
}

export interface UpdateTelecallerRequest {
  email?: string;
  name?: string;
  age?: number;
  gender?: string;
  bio?: string;
  role_id?: string;
  role?: string;
  contact_number?: string;
  avatar_image?: string;
  telecaller_status?: string;
}

export interface UpdateTelecallerStatusRequest {
  telecaller_status: 'active' | 'deactive';
}

export interface AssignProgramsRequest {
  program_ids: string[];
}

export interface AssignPatientsRequest {
  patient_ids: string[];
}

export interface TelecallerResponse {
  id: string;
  user_id: string;
  name: string;
  email: string;
  age?: number | null;
  gender?: string | null;
  bio?: string | null;
  contact_number?: string;
  avatar_image?: string | null;
  role: string;
  role_id: string;
  organization_id: string;
  telecaller_status: string;
  created_at: string;
  privileges: string[];
  no_of_patients: number;
  no_of_programs: number;
  total_purchases: number;
  total_net_amount: number;
  average_order_value: number;
  programs: ProgramDetail[];
}

export interface TelecallersListResponse {
  users: TelecallerResponse[];
  total: number;
  page: number;
  limit: number;
}

/**
 * IMPORTANT: All methods in this service expect the database ID (e.g., "693966b2fce718b4c338afa6")
 * NOT the display user_id (e.g., "USER9255").
 *
 * The API response contains two ID fields:
 * - "id": Database ID - USE THIS for all API calls
 * - "user_id": Display ID (e.g., "USER1234") - USE THIS only for UI display
 */
@Injectable({ providedIn: 'root' })
export class TelecallersApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all telecallers with pagination
   */
  getTelecallers(
    page: number = 1,
    limit: number = 100,
    search?: string,
    hq?: string,
    city?: string,
    state?: string
  ): Observable<TelecallersListResponse> {
    let params: any = { page: page.toString(), limit: limit.toString() };
    if (search) params.search = search;
    if (hq) params.hq = hq;
    if (city) params.city = city;
    if (state) params.state = state;
    return this.http.get<TelecallersListResponse>(`${this.apiUrl}/telecallers/`, { params });
  }

  /**
   * Get a telecaller by database ID with full details
   * GET /api/v1/telecallers/{id}
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  getTelecallerById(telecallerId: string): Observable<TelecallerDetailResponse> {
    return this.http.get<TelecallerDetailResponse>(`${this.apiUrl}/telecallers/${telecallerId}`);
  }

  /**
   * Get telecaller overview metrics
   * GET /api/v1/telecallers/{id}/overview
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  getTelecallerOverview(telecallerId: string): Observable<TelecallerOverviewResponse> {
    return this.http.get<TelecallerOverviewResponse>(`${this.apiUrl}/telecallers/${telecallerId}/overview`);
  }

  /**
   * Get telecaller sales report
   * GET /api/v1/telecallers/{id}/sales-report
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   * @param page - Page number for pagination (optional)
   * @param limit - Number of items per page (optional)
   */
  getTelecallerSalesReport(
    telecallerId: string,
    page?: number,
    limit?: number
  ): Observable<SalesReportResponse> {
    let url = `${this.apiUrl}/telecallers/${telecallerId}/sales-report`;
    const params: string[] = [];

    if (page !== undefined) {
      params.push(`page=${page}`);
    }
    if (limit !== undefined) {
      params.push(`limit=${limit}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return this.http.get<SalesReportResponse>(url);
  }

  /**
   * Get telecaller call logs
   * GET /api/v1/telecallers/{id}/call-logs
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   * @param startTime - Filter calls after this time (ISO 8601 format) (optional)
   * @param endTime - Filter calls before this time (ISO 8601 format) (optional)
   * @param status - Filter by call status (completed, busy, failed, no-answer) (optional)
   * @param direction - Filter by direction (inbound or outbound) (optional)
   * @param maxRecords - Maximum number of call records to fetch (optional)
   */
  getTelecallerCallLogs(
    telecallerId: string,
    startTime?: string,
    endTime?: string,
    status?: 'completed' | 'busy' | 'failed' | 'no-answer',
    direction?: 'inbound' | 'outbound',
    maxRecords?: number
  ): Observable<CallLogsResponse> {
    let url = `${this.apiUrl}/telecallers/${telecallerId}/call-logs`;
    const params: string[] = [];

    if (startTime) {
      params.push(`start_time=${encodeURIComponent(startTime)}`);
    }
    if (endTime) {
      params.push(`end_time=${encodeURIComponent(endTime)}`);
    }
    if (status) {
      params.push(`status=${status}`);
    }
    if (direction) {
      params.push(`direction=${direction}`);
    }
    if (maxRecords !== undefined) {
      params.push(`max_records=${maxRecords}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return this.http.get<CallLogsResponse>(url);
  }

  /**
   * Create a new telecaller
   */
  createTelecaller(data: CreateTelecallerRequest): Observable<TelecallerResponse> {
    return this.http.post<TelecallerResponse>(`${this.apiUrl}/telecallers/`, data);
  }

  /**
   * Update an existing telecaller
   * PATCH /api/v1/telecallers/{id}
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  updateTelecaller(telecallerId: string, data: UpdateTelecallerRequest): Observable<TelecallerResponse> {
    return this.http.patch<TelecallerResponse>(`${this.apiUrl}/telecallers/${telecallerId}`, data);
  }

  /**
   * Update telecaller status (active/deactive)
   * PATCH /api/v1/telecallers/{id}/status
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  updateTelecallerStatus(telecallerId: string, status: 'active' | 'deactive'): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/telecallers/${telecallerId}/status`, {
      telecaller_status: status,
    });
  }

  /**
   * Delete a telecaller
   * DELETE /api/v1/telecallers/{id}
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  deleteTelecaller(telecallerId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/telecallers/${telecallerId}`);
  }

  /**
   * Assign programs to a telecaller
   * POST /api/v1/telecallers/{id}/programs
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  assignPrograms(telecallerId: string, programIds: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/telecallers/${telecallerId}/programs`, {
      program_ids: programIds,
    });
  }

  /**
   * Assign patients to a telecaller
   * POST /api/v1/telecallers/{id}/patients
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  assignPatients(telecallerId: string, patientIds: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/telecallers/${telecallerId}/patients`, {
      patient_ids: patientIds,
    });
  }

  /**
   * Get patients assigned to a telecaller with pagination and search
   * GET /api/v1/telecallers/{id}/patients
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  getTelecallerPatients(
    telecallerId: string,
    page?: number,
    limit?: number,
    search?: string,
    dateFilter?: 'day' | 'week' | 'month' | 'custom',
    startDate?: string,
    endDate?: string
  ): Observable<TelecallerPatientsResponse> {
    let url = `${this.apiUrl}/telecallers/${telecallerId}/patients`;
    const params: string[] = [];

    if (page !== undefined) {
      params.push(`page=${page}`);
    }
    if (limit !== undefined) {
      params.push(`limit=${limit}`);
    }
    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
    }
    if (dateFilter) {
      params.push(`date_filter=${dateFilter}`);
    }
    if (startDate) {
      params.push(`start_date=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      params.push(`end_date=${encodeURIComponent(endDate)}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return this.http.get<TelecallerPatientsResponse>(url);
  }

  /**
   * Get programs assigned to a telecaller
   * GET /api/v1/telecallers/{id}/programs
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  getTelecallerPrograms(telecallerId: string): Observable<TelecallerProgramsResponse> {
    return this.http.get<TelecallerProgramsResponse>(`${this.apiUrl}/telecallers/${telecallerId}/programs`);
  }

  /**
   * Unassign a program from a telecaller
   * DELETE /api/v1/telecallers/{id}/programs/{program_id}
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  unassignProgram(telecallerId: string, programId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/telecallers/${telecallerId}/programs/${programId}`);
  }

  /**
   * @deprecated Use getTelecallerPatients instead
   * Get patients assigned to a telecaller
   * @param telecallerId - The database ID (e.g., "693966b2fce718b4c338afa6"), NOT the display ID
   */
  getPatientsByTelecallerId(telecallerId: string, page: number, limit: number): Observable<any> {
    return this.getTelecallerPatients(telecallerId, page, limit);
  }

  /**
   * Get telecaller analytics
   * GET /api/v1/dashboard/analytics/telecaller/{telecaller_id}
   */
  getTelecallerAnalytics(telecallerId: string, startDate?: string, endDate?: string): Observable<TelecallerAnalyticsResponse> {
    let params: any = {};
    
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    return this.http.get<TelecallerAnalyticsResponse>(
      `${this.apiUrl}/dashboard/analytics/telecaller/${telecallerId}`,
      { params }
    );
  }

  /**
   * Get upcoming follow-ups for the current telecaller
   * GET /api/v1/telecallers/me/upcoming-followups
   */
  getUpcomingFollowups(
    filterType: string = 'today',
    limit: number = 50,
    startDate?: string,
    endDate?: string,
  ): Observable<UpcomingFollowupsResponse> {
    let normalizedFilter = filterType;
    if (normalizedFilter === 'week') normalizedFilter = 'this_week';
    if (normalizedFilter === 'month') normalizedFilter = 'this_month';
    const params: Record<string, string> = { filter_type: normalizedFilter, limit: limit.toString() };
    if (normalizedFilter === 'custom') {
      // Backend parses these as datetimes, so span the whole of both selected days.
      if (startDate) params['start_date'] = `${startDate}T00:00:00`;
      if (endDate) params['end_date'] = `${endDate}T23:59:59`;
    }
    return this.http.get<UpcomingFollowupsResponse>(`${this.apiUrl}/telecallers/me/upcoming-followups`, { params });
  }

  /**
   * Get telecaller performance metrics
   * GET /api/v1/telecallers/me/performance
   */
  getTelecallerPerformance(period: string = 'all'): Observable<TelecallerPerformanceResponse> {
    const params = { period };
    return this.http.get<TelecallerPerformanceResponse>(`${this.apiUrl}/telecallers/me/performance`, { params });
  }

  /**
   * Get telecaller verification status
   * GET /api/v1/telecallers/{id}/verification-status
   */
  getVerificationStatus(telecallerId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/telecallers/${telecallerId}/verification-status`);
  }
}


